(() => {
  'use strict';

  const ROOT_ID = 'mg-draft-save-root';
  const TEE_FORM = '#tee-artwork-form';
  const TEE_INPUTS = '#teeFormInputs';
  const TEE_ACTIONS = '.tee-form-actions.tee-block, .tee-form-actions';
  const PREVIEW_SELECTORS = [
    '.tee-slide--active .tee-mockup-artwork',
    '.tee-mockup-artwork',
    '.tee-slide--active .tee-printarea-artwork'
  ];
  let capturedOriginalFile = null;

  const rememberOriginal = (value) => {
    if (value instanceof File && value.type.startsWith('image/') && !/^entwurf-vorschau\./i.test(value.name || '')) capturedOriginalFile = value;
  };

  // Teeinblue verwendet je nach Upload-Dialog versteckte oder kurzlebige
  // Datei-Inputs. Diese beiden Browser-Wege erfassen trotzdem das Original-File.
  const nativeReadAsDataURL = FileReader.prototype.readAsDataURL;
  FileReader.prototype.readAsDataURL = function (blob) {
    rememberOriginal(blob);
    return nativeReadAsDataURL.call(this, blob);
  };
  const nativeCreateObjectURL = URL.createObjectURL.bind(URL);
  URL.createObjectURL = function (blob) {
    rememberOriginal(blob);
    return nativeCreateObjectURL(blob);
  };
  const nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    if (init?.body instanceof FormData) {
      for (const value of init.body.values()) rememberOriginal(value);
    }
    return nativeFetch(input, init);
  };
  const nativeXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body) {
    if (body instanceof FormData) {
      for (const value of body.values()) rememberOriginal(value);
    }
    return nativeXhrSend.call(this, body);
  };

  // Die unveränderte Kundendatei direkt beim Auswählen sichern. Teeinblue
  // ersetzt die Upload-Ansicht später teilweise durch eine nicht abrufbare URL.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;
    const image = [...(input.files || [])].find((file) => file.type.startsWith('image/'));
    rememberOriginal(image);
  }, true);

  const text = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const labelFor = (root, input) => input.id ? root.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
  const fieldTitle = (input) => text(input.closest('.tee-field')?.querySelector('.tee-field__heading')?.textContent) || input.name || input.id;

  function friendlyFontName(root, input) {
    const label = labelFor(root, input);
    const candidates = [
      label?.dataset.title,
      label?.getAttribute('title'),
      label?.getAttribute('aria-label'),
      input.dataset.title,
      input.getAttribute('title'),
      text(label?.textContent),
      input.value,
      label?.querySelector('*') ? getComputedStyle(label.querySelector('*')).fontFamily : '',
      label ? getComputedStyle(label).fontFamily : ''
    ].map(text).filter(Boolean);
    let value = candidates.find((candidate) => !/^(abc|auswählen)$/i.test(candidate)) || input.value;
    value = String(value).split(',')[0].replace(/["']/g, '').trim();
    return value
      .replace(/[-_]?regular$/i, '')
      .replace(/[-_]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function selectedLabel(root, input) {
    const label = labelFor(root, input);
    return text(label?.dataset.title || label?.getAttribute('title') || label?.textContent || input.value);
  }

  function collectPersonalization() {
    const root = document.querySelector(TEE_INPUTS);
    if (!root) throw new Error('Der Personalisierer ist noch nicht vollständig geladen.');

    const result = { fields: {}, fonts: {}, colors: {}, options: {}, visibility: {} };
    const controls = [...root.querySelectorAll('input, textarea, select')];

    let latestTextField = '';
    controls.forEach((input) => {
      if (input.disabled || input.type === 'file' || input.id === 'confirmed') return;
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;

      const title = fieldTitle(input);
      const value = input.type === 'checkbox' ? Boolean(input.checked) :
        (input.type === 'radio' ? selectedLabel(root, input) : text(input.value));
      if (value === '' || value === false) return;

      if (/-font$/.test(input.name || '') || /-font-/.test(input.id || '') || /schriftart|font/i.test(title)) {
        const genericTitle = /^(schriftart\s*)?(auswählen|wählen)?\s*$/i.test(title);
        let fontField = genericTitle ? latestTextField : title.replace(/schriftart|auswählen/gi, '').trim();
        if (!fontField) fontField = latestTextField || `Textfeld ${Object.keys(result.fonts).length + 1}`;
        let uniqueField = fontField;
        let suffix = 2;
        while (Object.prototype.hasOwnProperty.call(result.fonts, uniqueField)) uniqueField = `${fontField} ${suffix++}`;
        result.fonts[uniqueField] = friendlyFontName(root, input);
      } else if (/color|farbe/i.test(`${input.name} ${input.id} ${title}`)) {
        result.colors[title] = value;
      } else if (/visibility|zeige/i.test(`${input.id} ${title}`)) {
        result.visibility[title] = value;
      } else if (input.matches('input[type="text"], input[type="date"], textarea')) {
        result.fields[title] = value;
        latestTextField = title.replace(/\*+$/, '').trim();
      } else {
        result.options[title] = value;
      }
    });

    return result;
  }

  function collectProduct(root) {
    const tee = document.querySelector(TEE_FORM);
    const shopifyForm = document.querySelector('form[action*="/cart/add"]');
    const variantInput = tee?.querySelector('input[name="id"]') || shopifyForm?.querySelector('input[name="id"]');
    const sizeInput = tee?.querySelector('.tee-option--size input:checked');
    const qualityInput = tee?.querySelector('#tee-field--teeinblue-available-prod input:checked');
    const selected = {};

    tee?.querySelectorAll('.tee-variants .tee-option').forEach((group) => {
      const heading = text(group.querySelector('.tee-option__title')?.textContent);
      const input = group.querySelector('input:checked, select');
      if (heading && input) selected[heading] = selectedLabel(group, input);
    });

    return {
      title: root.dataset.productTitle,
      productId: root.dataset.productId,
      variantId: variantInput?.value || '',
      url: window.location.href,
      price: text(tee?.querySelector('.tee-price--current')?.textContent || document.querySelector('[data-product-price]')?.textContent),
      size: sizeInput ? selectedLabel(tee, sizeInput) : '',
      quality: qualityInput ? selectedLabel(tee, qualityInput) : '',
      selectedOptions: selected
    };
  }

  async function findOriginalImage() {
    const tee = document.querySelector(TEE_FORM) || document.querySelector(TEE_INPUTS);
    const files = [...(tee?.querySelectorAll('input[type="file"]') || [])]
      .flatMap((input) => [...(input.files || [])])
      .filter((file) => file.type.startsWith('image/'));
    if (files[0]) return { file: files[0], url: '' };
    if (capturedOriginalFile) return { file: capturedOriginalFile, url: '' };

    const uploadedArtworkImage = [...document.querySelectorAll('.tee-mockup-artwork image')]
      .map((image) => image.getAttribute('href') || image.getAttribute('xlink:href') || '')
      .find((url) => /\/customers\/uploads\//i.test(url));
    if (/^https:\/\//.test(uploadedArtworkImage || '')) return { file: null, url: uploadedArtworkImage };

    const photos = [...(tee?.querySelectorAll('.tee-photo img, .tee-layer-photo img, [class*="photo"] img, [class*="upload"] img') || [])];
    const photo = photos.find((img) => /^(blob:|data:image\/|https:\/\/)/.test(img.currentSrc || img.src || img.dataset?.src || ''));
    const url = photo?.currentSrc || photo?.src || photo?.dataset?.src || '';
    return { file: null, url: /^https:\/\//.test(url) ? url : '' };
  }

  async function imageAsDataUrl(url) {
    if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url;
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(`Bild konnte nicht geladen werden (${response.status}).`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function exportPreview() {
    const source = PREVIEW_SELECTORS.map((selector) => document.querySelector(selector)).find(Boolean);
    if (!source) throw new Error('Die aktuelle Vorschau wurde nicht gefunden.');

    const rect = source.getBoundingClientRect();
    if (!rect.width || !rect.height) throw new Error('Die aktuelle Vorschau ist nicht sichtbar.');

    const canvases = [source, ...source.querySelectorAll('canvas')]
      .filter((node) => node instanceof HTMLCanvasElement && node.width && node.height && node.getBoundingClientRect().width);
    if (canvases.length) {
      const output = document.createElement('canvas');
      const scale = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
      output.width = Math.round(rect.width * scale);
      output.height = Math.round(rect.height * scale);
      const context = output.getContext('2d');
      context.scale(scale, scale);
      canvases.forEach((canvas) => {
        const layer = canvas.getBoundingClientRect();
        context.drawImage(canvas, layer.left - rect.left, layer.top - rect.top, layer.width, layer.height);
      });
      return await new Promise((resolve, reject) => output.toBlob(
        (blob) => blob ? resolve(new File([blob], 'entwurf-vorschau.png', { type: 'image/png' })) : reject(new Error('Die Canvas-Vorschau konnte nicht exportiert werden.')),
        'image/png'
      ));
    }

    const previewImages = [source, ...source.querySelectorAll('img')]
      .filter((node) => node instanceof HTMLImageElement && node.getBoundingClientRect().width && node.getBoundingClientRect().height)
      .sort((a, b) => (b.getBoundingClientRect().width * b.getBoundingClientRect().height) - (a.getBoundingClientRect().width * a.getBoundingClientRect().height));
    if (previewImages[0]) {
      const url = previewImages[0].currentSrc || previewImages[0].src;
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`Vorschaubild konnte nicht geladen werden (${response.status}).`);
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) throw new Error('Die Vorschauquelle ist kein Bild.');
      return new File([blob], 'entwurf-vorschau.png', { type: blob.type });
    }

    const clone = source.cloneNode(true);
    const originals = [source, ...source.querySelectorAll('*')];
    const clones = [clone, ...clone.querySelectorAll('*')];
    originals.forEach((node, index) => {
      const target = clones[index];
      const style = getComputedStyle(node);
      target.setAttribute('style', [...style].map((name) => `${name}:${style.getPropertyValue(name)};`).join(''));
    });

    const images = [...clone.querySelectorAll('img, image')];
    await Promise.all(images.map(async (img) => {
      const url = img.currentSrc || img.src || img.getAttribute('href') || img.getAttribute('xlink:href') || img.dataset?.src;
      if (url) {
        try {
          const dataUrl = await imageAsDataUrl(url);
          if (img instanceof SVGImageElement) {
            img.setAttribute('href', dataUrl);
            img.removeAttribute('xlink:href');
          } else {
            img.src = dataUrl;
          }
        } catch (_) {
          // Die vollständige SVG-Datei bleibt als zuverlässiger Fallback erhalten.
        }
      }
      img.removeAttribute('srcset');
    }));

    clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    const serialized = new XMLSerializer().serializeToString(clone);
    const svg = source instanceof SVGElement
      ? serialized.replace('xmlns="http://www.w3.org/1999/xhtml"', 'xmlns="http://www.w3.org/2000/svg"')
      : `<svg xmlns="http://www.w3.org/2000/svg" width="${rect.width}" height="${rect.height}" viewBox="0 0 ${rect.width} ${rect.height}"><foreignObject width="100%" height="100%">${serialized}</foreignObject></svg>`;
    const svgUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));

    try {
      const image = new Image();
      image.src = svgUrl;
      try {
        await image.decode();
      } catch (_) {
        return new File([svg], 'entwurf-vorschau.svg', { type: 'image/svg+xml' });
      }
      const scale = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(rect.width * scale);
      canvas.height = Math.round(rect.height * scale);
      const context = canvas.getContext('2d');
      context.scale(scale, scale);
      context.drawImage(image, 0, 0, rect.width, rect.height);
      return await new Promise((resolve, reject) => canvas.toBlob(
        (blob) => blob ? resolve(new File([blob], 'entwurf-vorschau.png', { type: 'image/png' })) : reject(new Error('Die Vorschau konnte nicht als PNG erzeugt werden.')),
        'image/png'
      ));
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  function initialise() {
    const root = document.getElementById(ROOT_ID);
    const teeInputs = document.querySelector(TEE_INPUTS);
    const teeActions = document.querySelector(TEE_ACTIONS);
    if (!root || !teeInputs || !teeActions) return false;

    if (!root.dataset.endpoint) {
      console.warn('Entwurf speichern: In den Theme-Einstellungen ist kein Endpoint hinterlegt.');
      return true;
    }

    const placeInActions = () => {
      const actions = document.querySelector(TEE_ACTIONS);
      if (!actions) return;
      if (root.parentElement !== actions) actions.prepend(root);
    };

    placeInActions();
    root.hidden = false;

    // Teeinblue baut den Aktionsbereich bei Variantenwechseln teilweise neu auf.
    // Der Button wird deshalb bei Bedarf wieder in denselben Sticky-Block eingesetzt.
    const placementObserver = new MutationObserver(placeInActions);
    placementObserver.observe(teeInputs.closest(TEE_FORM) || document.body, { childList: true, subtree: true });

    const dialog = root.querySelector('dialog');
    const form = root.querySelector('form');
    const submit = root.querySelector('.mg-draft-save__submit');
    const status = root.querySelector('.mg-draft-save__status');
    root.querySelector('.mg-draft-save__open').addEventListener('click', () => dialog.showModal());
    root.querySelector('.mg-draft-save__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submit.disabled || !form.reportValidity()) return;
      submit.disabled = true;
      submit.querySelector('span').textContent = 'Entwurf wird gespeichert …';
      status.className = 'mg-draft-save__status';
      status.textContent = 'Daten und Vorschau werden vorbereitet …';

      try {
        const original = await findOriginalImage();
        let preview = null;
        let previewError = '';
        try { preview = await exportPreview(); } catch (error) { previewError = error.message; }

        const payload = {
          schemaVersion: 1,
          customer: { email: text(form.elements.namedItem('email').value) },
          product: collectProduct(root),
          personalization: collectPersonalization(),
          assets: { originalImageUrl: original.url, previewExportError: previewError },
          consent: { granted: true, textVersion: 1 },
          metadata: { createdAt: new Date().toISOString(), userAgent: navigator.userAgent }
        };

        const body = new FormData();
        body.append('draft', new Blob([JSON.stringify(payload)], { type: 'application/json' }), 'entwurf.json');
        if (original.file) body.append('originalImage', original.file, original.file.name);
        if (preview) body.append('previewImage', preview, preview.name);

        const response = await fetch(root.dataset.endpoint, { method: 'POST', body, credentials: 'omit', headers: { Accept: 'application/json' } });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.message || 'Der Entwurf konnte nicht übermittelt werden.');

        status.className = 'mg-draft-save__status is-success';
        status.textContent = 'Dein Entwurf wurde gespeichert. Du kannst deine Gestaltung jetzt weiter bearbeiten oder deine Gedenktafel direkt bestellen.';
        form.elements.namedItem('email').value = '';
        form.elements.namedItem('consent').checked = false;
      } catch (error) {
        status.className = 'mg-draft-save__status is-error';
        status.textContent = error.message || 'Die Übermittlung ist fehlgeschlagen. Bitte versuche es erneut.';
      } finally {
        submit.disabled = false;
        submit.querySelector('span').textContent = 'Entwurf speichern';
      }
    });
    return true;
  }

  function start() {
    let attempts = 0;
    if (initialise()) return;
    const timer = setInterval(() => { attempts += 1; if (initialise() || attempts >= 80) clearInterval(timer); }, 250);
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start, { once: true }) : start();
})();
